import { useState } from "react";
import { regions, years, genres, sortOptions } from "@/mocks/categories";

export type Filters = {
  region: string;
  year: string;
  genre: string;
};

type Props = {
  filters: Filters;
  onChange: (filters: Filters) => void;
  sort: string;
  onSortChange: (sort: string) => void;
  onReset: () => void;
};

const groups: { key: keyof Filters; label: string; options: string[] }[] = [
  { key: "region", label: "地区", options: regions },
  { key: "year", label: "年份", options: years },
  { key: "genre", label: "类型", options: genres },
];

function ChipRow({
  label,
  options,
  value,
  onSelect,
}: {
  label: string;
  options: string[];
  value: string;
  onSelect: (v: string) => void;
}) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-background-100 last:border-0">
      <span className="w-12 shrink-0 pt-1.5 text-sm text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onSelect(opt)}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
              value === opt
                ? "bg-primary-600 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({ filters, onChange, sort, onSortChange, onReset }: Props) {
  const [open, setOpen] = useState(false);

  const setValue = (key: keyof Filters, v: string) => onChange({ ...filters, [key]: v });

  const activeCount = [filters.region, filters.year, filters.genre].filter((x) => x !== "全部").length;

  return (
    <div>
      {/* Desktop / tablet filter */}
      <div className="hidden md:block rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
        {groups.map((g) => (
          <ChipRow
            key={g.key}
            label={g.label}
            options={g.options}
            value={filters[g.key]}
            onSelect={(v) => setValue(g.key, v)}
          />
        ))}
      </div>

      {/* Mobile filter button + sort */}
      <div className="md:hidden flex items-center gap-3">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 cursor-pointer"
        >
          <i className="ri-filter-3-line" aria-hidden="true"></i>
          筛选
          {activeCount > 0 && (
            <span className="w-5 h-5 rounded-full bg-accent-500 text-primary-950 text-xs flex items-center justify-center">
              {activeCount}
            </span>
          )}
        </button>
        <SortSelect sort={sort} onSortChange={onSortChange} />
      </div>

      {/* Desktop sort row */}
      <div className="hidden md:flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          {sortOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => onSortChange(opt.key)}
              className={`px-4 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                sort === opt.key
                  ? "bg-accent-500 text-primary-950"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {opt.name}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onReset}
          className="text-sm text-foreground-500 hover:text-accent-600 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-refresh-line" aria-hidden="true"></i>
          重置筛选
        </button>
      </div>

      {open && (
        <div className="md:hidden fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-primary-950/50"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="relative w-full max-h-[80vh] overflow-y-auto bg-background-50 rounded-t-2xl p-5 animate-fade-up">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-semibold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="关闭筛选"
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 text-xl"
              >
                <i className="ri-close-line" aria-hidden="true"></i>
              </button>
            </div>
            {groups.map((g) => (
              <ChipRow
                key={g.key}
                label={g.label}
                options={g.options}
                value={filters[g.key]}
                onSelect={(v) => setValue(g.key, v)}
              />
            ))}
            <div className="flex gap-3 mt-4">
              <button
                type="button"
                onClick={onReset}
                className="flex-1 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium cursor-pointer"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SortSelect({ sort, onSortChange }: { sort: string; onSortChange: (s: string) => void }) {
  return (
    <div className="relative">
      <select
        value={sort}
        onChange={(e) => onSortChange(e.target.value)}
        aria-label="排序方式"
        className="appearance-none pl-4 pr-9 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 cursor-pointer focus:outline-none"
      >
        {sortOptions.map((opt) => (
          <option key={opt.key} value={opt.key}>
            {opt.name}
          </option>
        ))}
      </select>
      <i
        className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none"
        aria-hidden="true"
      ></i>
    </div>
  );
}